import * as React from "react";
import {Link, NavLink, useLocation} from "react-router";
import {useSelector} from "react-redux";

import {ApplicationState} from "../../store";
import {commonConstant} from "../../helpers/constants";
import {useBackgroundPromiseWrapper} from "../../helpers/hooks/useBackgroundPromiseWrapper";
import {useKeycloak} from "../../contexts/KeycloakContext";
import {useOutsideClickHandler} from "../../helpers/hooks/useOutsideClickHandler";


interface HeaderItemProps {
    url: string;
    label: string;
    icon: string;
    extraActivePaths?: string[];
}


export const Header: React.FC = () => {
    const [promiseWrapper] = useBackgroundPromiseWrapper();
    const user = useSelector((appState: ApplicationState) => appState.context.userContext);
    const {authenticated, login, logout} = useKeycloak();
    const isChildAccount = user?.OwnerId != null;
    const location = useLocation();
    const navItems: HeaderItemProps[] = [
        {label: `Документация`, icon: `fas fa-folder-open`, url: `/projectdocs`, extraActivePaths: [`/`]},
        {label: `Отчеты`, icon: `fas fa-chart-column`, url: `/reports`},
        {label: `Типовые проекты и решения`, icon: `fas fa-diagram-project`, url: `/standartsolutions`},
    ];
    const faqItem: HeaderItemProps = {label: `Помощь`, icon: `fa-regular fa-circle-question`, url: `/faq`};
    const isItemActive = (it: HeaderItemProps) => it.extraActivePaths?.includes(location.pathname) ?? false;

    const userTitle = user.Title || user.Login;
    const userInitial = (userTitle || `?`).trim().charAt(0).toUpperCase();

    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
    const onToggleDrawer = () => setDrawerOpen(prev => !prev);
    const closeDrawer = () => setDrawerOpen(false);

    React.useEffect(() => {
        document.body.style.overflow = drawerOpen ? `hidden` : ``;
        return () => {
            document.body.style.overflow = ``;
        };
    }, [drawerOpen]);

    const [userMenuOpen, setUserMenuOpen] = React.useState<boolean>(false);
    const userMenuRef = React.useRef<HTMLDivElement>(null);
    useOutsideClickHandler(userMenuRef, React.useCallback(() => setUserMenuOpen(false), []));
    const onToggleUserMenu = (e) => {
        e.preventDefault();
        setUserMenuOpen(prev => !prev);
    }

    const signOut = React.useCallback((e) => {
        e.preventDefault();
        setUserMenuOpen(false);
        closeDrawer();
        logout();
        // eslint-disable-next-line
    }, [logout]);

    const onLogin = (e) => {
        e.preventDefault();
        closeDrawer();
        login();
    }

    const navLinkClassName = (it: HeaderItemProps) => ({isActive}: { isActive: boolean }) =>
        `app-header__nav-link${isActive || isItemActive(it) ? ` app-header__nav-link--active` : ``}`;
    const drawerLinkClassName = (it: HeaderItemProps) => ({isActive}: { isActive: boolean }) =>
        `app-header__drawer-link${isActive || isItemActive(it) ? ` app-header__drawer-link--active` : ``}`;

    return (
        <header className="app-header">
            <div className="app-header__bar">
                <div className={'app-header__logo'}>
                    <button type="button" className="app-header__burger" onClick={onToggleDrawer} aria-label="Меню">
                        <i className="fas fa-bars"></i>
                    </button>
                    <Link className="app-header__brand" title={commonConstant.companyName} to="/">
                        <img src="logo.png" alt={commonConstant.companyName}/>
                    </Link></div>

                {authenticated && (
                    <>
                        <div className="app-header__divider"/>
                        <nav className="app-header__nav">
                            {navItems.map((it, idx) => (
                                <NavLink key={idx} to={it.url} className={navLinkClassName(it)}>
                                    <i className={it.icon}></i>
                                    <span>{it.label}</span>
                                </NavLink>
                            ))}
                        </nav>
                    </>
                )}

                <div className="app-header__actions">
                    {authenticated ? (
                        <>
                            <NavLink to={faqItem.url} className="app-header__icon-btn" title={faqItem.label}>
                                <i className={faqItem.icon}></i>
                            </NavLink>
                            <div className="app-header__divider"/>
                            <div className="app-header__user" ref={userMenuRef}>
                                <a
                                    href="# "
                                    className={`app-header__user-trigger${userMenuOpen ? ` app-header__user-trigger--open` : ``}`}
                                    onClick={onToggleUserMenu}
                                >
                                    <span className="app-header__avatar">{userInitial}</span>
                                    <span className="app-header__user-name">{userTitle}</span>
                                    <i className="fas fa-chevron-down"></i>
                                </a>
                                <a
                                    href="# "
                                    className={`app-header__user-trigger-mobile${userMenuOpen ? ` app-header__user-trigger-mobile--open` : ``}`}
                                    onClick={onToggleUserMenu}
                                >
                                    <span className="app-header__avatar">{userInitial}</span>
                                    <i className="fas fa-chevron-down"></i>
                                </a>
                                {userMenuOpen && (
                                    <div className="app-header__dropdown">
                                        {!isChildAccount && (
                                            <Link className="app-header__dropdown-item" to="/my-users"
                                                  onClick={() => setUserMenuOpen(false)}>
                                                <i className="fas fa-users"></i> Мои пользователи
                                            </Link>
                                        )}
                                        <Link className="app-header__dropdown-item" to="/profile"
                                              onClick={() => setUserMenuOpen(false)}>
                                            <i className="fas fa-user"></i> Профиль
                                        </Link>
                                        <div className="app-header__dropdown-divider"/>
                                        <a className="app-header__dropdown-item app-header__dropdown-item--danger"
                                           href="# " onClick={signOut}>
                                            <i className="fas fa-sign-out-alt"></i> Выйти
                                        </a>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <a href="# " className="app-header__link" onClick={onLogin}>
                                <i className="fas fa-sign-in-alt"></i>
                                <span>Войти</span>
                            </a>
                            <Link to="/registration" className="app-header__link app-header__link--primary">
                                <i className="fas fa-user-plus"></i>
                                <span>Регистрация</span>
                            </Link>
                        </>
                    )}
                </div>
            </div>

            {drawerOpen && (
                <div className="app-header__drawer-overlay" onClick={closeDrawer}>
                    <div className="app-header__drawer" onClick={(e) => e.stopPropagation()}>
                        <div className="app-header__drawer-header">
                            <img src="logo.png" alt={commonConstant.companyName}/>
                            <button type="button" className="app-header__drawer-close" onClick={closeDrawer}
                                    aria-label="Закрыть">
                                <i className="fas fa-xmark"></i>
                            </button>
                        </div>

                        {authenticated ? (
                            <>
                                <nav className="app-header__drawer-nav">
                                    {[...navItems, faqItem].map((it, idx) => (
                                        <NavLink key={idx} to={it.url} className={drawerLinkClassName(it)}
                                                 onClick={closeDrawer}>
                                            <i className={it.icon}></i>
                                            <span>{it.label}</span>
                                        </NavLink>
                                    ))}
                                </nav>
                                <div className="app-header__drawer-user">
                                    <span className="app-header__avatar">{userInitial}</span>
                                    <div className="app-header__drawer-user-info">
                                        <span className="app-header__drawer-user-name">{userTitle}</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <nav className="app-header__drawer-nav">
                                <a href="# " className="app-header__drawer-link" onClick={onLogin}>
                                    <i className="fas fa-sign-in-alt"></i>
                                    <span>Войти</span>
                                </a>
                                <Link to="/registration" className="app-header__drawer-link" onClick={closeDrawer}>
                                    <i className="fas fa-user-plus"></i>
                                    <span>Регистрация</span>
                                </Link>
                            </nav>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};
